import { useEffect, useRef } from "react";
import { isEmbedded } from "@/Utils/superops/embedDetection";
import useDocumentScript from "@/Hooks/useDocumentScript";
import { SuperOpsMessageService } from "../services/SuperOpsMessageService";
import { SuperOpsSdkService } from "../services/SuperOpsSdkService";
import { resolveSuperOpsWidgetName } from "../utils/SuperOpsRouteDetection";
import { SUPEROPS_CONFIG } from "../config/SuperOpsConfig";

/**
 * Custom hook for handling embedded mode functionality
 * Loads + initializes the SuperOps SDK, wires the state-restore/persist bridge,
 * and applies the embedded CSS class when running inside the SuperOps host.
 */
export const useEmbeddedMode = (): { isEmbeddedMode: boolean } => {
	const isEmbeddedMode = isEmbedded();
	const addScriptToDocument = useDocumentScript();
	const sdkRequestedRef = useRef(false);

	useEffect(() => {
		if (!isEmbeddedMode) {
			return undefined;
		}

		// Add embedded CSS class to body
		document.body.classList.add(SUPEROPS_CONFIG.CSS_CLASSES.EMBEDDED_MODE);

		const widgetName = resolveSuperOpsWidgetName();
		const sdkUrl = process.env.REACT_APP_SUPEROPS_SDK_URL;

		const startBridge = () => {
			if (widgetName) {
				SuperOpsSdkService.init(widgetName);
			}
			// Registers SDK restore/persist handlers (now that init swapped in the
			// full SDK API) and runs the post-login pending-restoration check.
			SuperOpsMessageService.getInstance().initialize();
		};

		if (widgetName && sdkUrl) {
			// Widget route with the SDK configured — load it, then init + bridge.
			if (!sdkRequestedRef.current) {
				sdkRequestedRef.current = true;
				addScriptToDocument(
					sdkUrl,
					null,
					startBridge,
					true,
					undefined,
					() =>
						// SDK failed to load (network/blocked) — still run the
						// sessionStorage-backed restoration bridge.
						SuperOpsMessageService.getInstance().initialize()
				);
			}
		} else {
			// Non-widget route (e.g. login) or SDK not configured — run the
			// sessionStorage-backed pending-restoration bridge without paying
			// the cost of downloading the SDK.
			SuperOpsMessageService.getInstance().initialize();
		}

		return () => {
			document.body.classList.remove(
				SUPEROPS_CONFIG.CSS_CLASSES.EMBEDDED_MODE
			);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEmbeddedMode]);

	return { isEmbeddedMode };
};
